import React from 'react';
import { WIDGET_ICON_MAP } from '../../../widgetIconMap';
import { formatShabbatTimeRule } from '../../shabbatZmanUtils';
import LeyningDisplay from '../../../shared/LeyningDisplay';

const Candle = ({ shabbat, processedCustomTimes, drashaText, loading, error, title = 'זמני שבת', showTitle = true, entryIcon = 'ShabbatCandles', exitIcon = 'MoonStar', dayEntryTime, dayExitTime, bgColor, showTimeRule = false, showEntryExit = true, shabbatLeyning, showLeyning, showHaftarah, leyningDisplayMode }) => {
  const bg = bgColor || '#1a1520';
  const EntryIcon = WIDGET_ICON_MAP[entryIcon] || WIDGET_ICON_MAP['ShabbatCandles'];
  const ExitIcon  = WIDGET_ICON_MAP[exitIcon]  || WIDGET_ICON_MAP['MoonStar'];

  const timeToMinutes = (t) => {
    if (!t || t === '--:--') return -1;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };

  if (loading) return <div style={{ ...S.wrap, backgroundColor: bg, justifyContent: 'center', alignItems: 'center' }}><span style={{ color: AMBER }}>טוען זמני שבת...</span></div>;
  if (error)   return <div style={{ ...S.wrap, backgroundColor: bg, justifyContent: 'center', alignItems: 'center' }}><span style={{ color: '#e57373' }}>{error}</span></div>;
  if (!shabbat) return <div style={{ ...S.wrap, backgroundColor: bg, justifyContent: 'center', alignItems: 'center' }}><span style={{ color: AMBER }}>אין נתוני שבת</span></div>;

  const parasha = shabbat.meta?.parsha || shabbat.parsha || 'פרשת השבוע';
  const entry   = dayEntryTime || shabbat.meta?.candleLighting || shabbat.friday?.candleLighting || '--:--';
  const exit    = dayExitTime  || shabbat.meta?.havdalah      || shabbat.shabbat?.havdalah      || '--:--';

  const fridayTimes  = (processedCustomTimes || []).filter(ct => (ct.day || 'friday') === 'friday').sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));
  const shabbatTimes = (processedCustomTimes || []).filter(ct => ct.day === 'shabbat').sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));
  const allCustom    = [...fridayTimes, ...shabbatTimes];

  return (
    <div style={{ ...S.wrap, backgroundColor: bg }} dir="rtl">

      {/* כותרת */}
      {showTitle && (
        <div style={S.header}>
          <span style={S.headerText}>{title}</span>
        </div>
      )}

      {/* פרשה */}
      <div style={S.parshaBlock}>
        {shabbat.meta?.parshaShowPrefix !== false && <div style={S.parshaLabel}>פרשת</div>}
        <div style={S.parshaName}>{parasha}</div>
        {shabbat.meta?.parshaSubtitle && <div style={S.parshaSubtitle}>{shabbat.meta.parshaSubtitle}</div>}
      </div>

      {/* קריאה */}
      {(showLeyning || showHaftarah) && shabbatLeyning && (
        <div style={{
          padding: '10px 14px',
          marginBottom: '12px',
          borderRadius: '8px',
          border: '1px solid rgba(212,130,10,0.25)',
          backgroundColor: 'rgba(212,130,10,0.06)',
        }}>
          <LeyningDisplay
            leyning={shabbatLeyning}
            mode={leyningDisplayMode}
            showHaftarah={showHaftarah}
            showMaftir={leyningDisplayMode === 'full'}
            compact={false}
            style={{ color: 'rgba(240,232,216,0.8)' }}
          />
        </div>
      )}

      {/* כניסת שבת — גוון ענבר/נרות */}
      {showEntryExit && (
        <div style={S.entryCard}>
          <div style={S.cardIconRow}>
            <div style={S.entryIconWrap}>
              <EntryIcon style={{ width: '24px', height: '24px', color: '#1a1520' }} />
            </div>
          </div>
          <div style={S.cardLabel}>כניסת שבת</div>
          <div style={{ ...S.cardTime, color: AMBER }}>{entry}</div>
        </div>
      )}

      {/* מפריד כוכבים */}
      {showEntryExit && (
        <div style={S.starRow}>
          <div style={S.starLine} />
          <span style={S.star}>✦</span>
          <span style={S.star}>✦</span>
          <span style={S.star}>✦</span>
          <div style={S.starLine} />
        </div>
      )}

      {/* צאת שבת — גוון ירח/כסף */}
      {showEntryExit && (
        <div style={S.exitCard}>
          <div style={S.cardIconRow}>
            <div style={S.exitIconWrap}>
              <ExitIcon style={{ width: '24px', height: '24px', color: '#1a1520' }} />
            </div>
          </div>
          <div style={{ ...S.cardLabel, color: 'rgba(176,198,216,0.75)' }}>צאת שבת</div>
          <div style={{ ...S.cardTime, color: SILVER }}>{exit}</div>
        </div>
      )}

      {/* דרשה */}
      {drashaText && (
        <div style={S.drashaBlock}>
          <div style={S.drashaText}>{drashaText}</div>
        </div>
      )}

      {/* זמנים מותאמים */}
      {allCustom.length > 0 && (
        <div style={S.customBlock}>
          {allCustom.map((ct, i) => (
            <div key={i} style={{ ...S.customRow, borderBottom: i < allCustom.length - 1 ? `1px solid rgba(176,198,216,0.12)` : 'none' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                <span style={S.customLabel}>{ct.label}</span>
                {showTimeRule && formatShabbatTimeRule(ct) && (
                  <span style={{ fontSize: '10px', color: 'rgba(240,232,216,0.4)', lineHeight: 1 }}>{formatShabbatTimeRule(ct)}</span>
                )}
              </div>
              <span style={S.customTime}>{ct.time}</span>
            </div>
          ))}
        </div>
      )}

    </div>
  );
};

const AMBER  = '#D4820A';
const SILVER = '#8FA8C8';

const S = {
  wrap: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    boxSizing: 'border-box',
    gap: '0px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '10px',
    paddingBottom: '10px',
    borderBottom: '1px solid rgba(212,130,10,0.25)',
  },
  headerText: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'rgba(212,130,10,0.7)',
    letterSpacing: '0.12em',
  },
  parshaBlock: {
    textAlign: 'center',
    marginBottom: '18px',
  },
  parshaLabel: {
    fontSize: '12px',
    color: 'rgba(212,130,10,0.55)',
    letterSpacing: '0.15em',
    marginBottom: '3px',
  },
  parshaName: {
    fontSize: '36px',
    fontWeight: '800',
    color: '#f0e8d8',
    lineHeight: 1.15,
    textShadow: '0 0 30px rgba(212,130,10,0.2)',
  },
  parshaSubtitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: 'rgba(240,232,216,0.75)',
    marginTop: '4px',
  },
  entryCard: {
    textAlign: 'center',
    padding: '14px 16px 16px',
    borderRadius: '10px',
    backgroundColor: 'rgba(212,130,10,0.1)',
    border: '1px solid rgba(212,130,10,0.3)',
    marginBottom: '8px',
  },
  cardIconRow: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '8px',
  },
  entryIconWrap: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    backgroundColor: AMBER,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 0 16px rgba(212,130,10,0.5)`,
  },
  exitCard: {
    textAlign: 'center',
    padding: '14px 16px 16px',
    borderRadius: '10px',
    backgroundColor: 'rgba(143,168,200,0.08)',
    border: '1px solid rgba(143,168,200,0.2)',
    marginBottom: '14px',
  },
  exitIconWrap: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    backgroundColor: SILVER,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 0 16px rgba(143,168,200,0.35)`,
  },
  cardLabel: {
    fontSize: '13px',
    color: 'rgba(212,130,10,0.7)',
    fontWeight: '600',
    letterSpacing: '0.05em',
    marginBottom: '4px',
  },
  cardTime: {
    fontSize: '44px',
    fontWeight: '800',
    direction: 'ltr',
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
  },
  starRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    margin: '4px 0 8px',
  },
  starLine: {
    flex: 1,
    height: '1px',
    backgroundColor: 'rgba(143,168,200,0.2)',
  },
  star: {
    fontSize: '10px',
    color: 'rgba(143,168,200,0.4)',
  },
  drashaBlock: {
    textAlign: 'center',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid rgba(212,130,10,0.2)',
    marginBottom: '12px',
  },
  drashaText: {
    fontSize: '15px',
    color: 'rgba(240,232,216,0.8)',
    fontStyle: 'italic',
    whiteSpace: 'pre-wrap',
    lineHeight: 1.5,
  },
  customBlock: {
    borderRadius: '8px',
    border: '1px solid rgba(176,198,216,0.15)',
    overflow: 'hidden',
  },
  customRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '9px 14px',
  },
  customLabel: {
    fontSize: '14px',
    color: 'rgba(240,232,216,0.55)',
  },
  customTime: {
    fontSize: '17px',
    fontWeight: '700',
    color: AMBER,
    direction: 'ltr',
  },
};

export default Candle;
